// OpenAI vision 格式的内容类型
export interface MessageContent {
    type: 'text' | 'image_url';
    text?: string;
    image_url?: {
        url: string;
        detail?: 'auto' | 'low' | 'high';
    };
}

export interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string | MessageContent[];
    metadata?: MessageMetadata;
}

export interface MessageMetadata {
    message_type: 'text' | 'voice' | 'image' | 'attachment' | 'location' | 'mixed';
    text_metadata?: {
        length: number;
        language?: string;
        encoding?: string;
    };
    voice_metadata?: {
        file_id?: string;
        url?: string;
        duration?: number;
        format?: string;
        size?: number;
        sample_rate?: number;
        channels?: number;
        transcription?: {
            text: string;
            confidence?: number;
            language?: string;
            provider?: string;
        };
    };
    image_metadata?: {
        file_id?: string;
        url: string;
        width?: number;
        height?: number;
        format?: string;
        size?: number;
        description?: string;
        alt_text?: string;
    };
    attachment_metadata?: {
        file_id?: string;
        filename: string;
        url: string;
        mime_type?: string;
        size?: number;
        description?: string;
        checksum?: string;
    };
    location_metadata?: {
        latitude: number;
        longitude: number;
        address?: string;
        poi_name?: string;
        accuracy?: number;
    };
}

export interface ChatCompletionRequest {
    messages: ChatMessage[];
    stream?: boolean;
    temperature?: number;
    max_tokens?: number;
    customerId?: string;
    sessionId?: string;
}

export interface ChatCompletionChunk {
    id: string;
    object: string;
    created: number;
    model: string;
    choices: Array<{
        index: number;
        text?: string;
        logprobs?: number | null;
        finish_reason?: string | null;
        seed?: number | null;
        delta: {
            token_id?: number | null;
            role?: string;
            content?: string;
            tool_calls?: any;
        };
        tool_calls?: any;
    }>;
    usage?: any;
}

// SessionID 管理工具
export class SessionManager {
    private static currentSessionId: string | null = null;

    // 生成UUID格式的SessionID
    static generateSessionId(): string {
        // 生成标准UUID v4格式
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    }

    // 初始化新会话
    static initSession(): string {
        this.currentSessionId = this.generateSessionId();
        console.log('新会话已初始化:', this.currentSessionId);
        return this.currentSessionId;
    }

    // 获取当前会话ID
    static getCurrentSessionId(): string {
        if (!this.currentSessionId) {
            this.currentSessionId = this.generateSessionId();
            console.log('自动生成会话ID:', this.currentSessionId);
        }
        return this.currentSessionId;
    }

    // 开始新会话
    static startNewSession(): string {
        const newSessionId = this.generateSessionId();
        this.currentSessionId = newSessionId;
        console.log('开始新会话:', newSessionId);
        return newSessionId;
    }

    // 清除当前会话
    static clearSession(): void {
        console.log('清除会话:', this.currentSessionId);
        this.currentSessionId = null;
    }
}

export class AIChatService {
    private baseURL = '/api/v1/chat/completions';

    async *streamChatCompletion(
        messages: ChatMessage[],
        options: {
            temperature?: number;
            max_tokens?: number;
            customerId?: string;
            sessionId?: string;
            onError?: (error: Error) => void;
        } = {}
    ): AsyncGenerator<string, void, unknown> {
        try {
            // 获取或生成sessionId
            const sessionId = options.sessionId || SessionManager.getCurrentSessionId();

            const requestBody: ChatCompletionRequest = {
                messages: messages.map(msg => ({
                    role: msg.role,
                    content: msg.content, // 现在支持 string 或 MessageContent[]
                    ...(msg.metadata && { metadata: msg.metadata })
                })),
                stream: true,
                temperature: options.temperature || 0.7,
                max_tokens: options.max_tokens || 2000,
                sessionId: sessionId,
                ...(options.customerId && { customerId: options.customerId }),
            };

            console.log('🚀 发送AI请求:', {
                url: this.baseURL,
                sessionId: sessionId,
                customerId: options.customerId,
                messagesCount: messages.length
            });

            const response = await fetch(this.baseURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            console.log('📡 收到响应:', {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries())
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ HTTP错误响应:', {
                    status: response.status,
                    statusText: response.statusText,
                    body: errorText
                });
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }

            if (!response.body) {
                console.error('❌ 响应体为空');
                throw new Error('Response body is null');
            }

            console.log('🔄 开始处理流式响应...');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            try {
                let buffer = '';
                let chunkCount = 0;
                let totalContentLength = 0;

                while (true) {
                    try {
                        const { done, value } = await reader.read();

                        if (done) {
                            console.log('✅ 流式响应完成，总共处理了', chunkCount, '个数据块，总内容长度:', totalContentLength);
                            break;
                        }

                        chunkCount++;
                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        if (chunkCount <= 5) {
                            console.log(`📦 收到数据块 ${chunkCount}:`, chunk.substring(0, 200) + (chunk.length > 200 ? '...' : ''));
                        }
                    } catch (readError) {
                        console.error('❌ 读取流数据时发生错误:', readError);
                        throw readError;
                    }

                    // 处理完整的行
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // 保留最后一个不完整的行

                    for (const line of lines) {
                        const trimmedLine = line.trim();

                        if (trimmedLine === '') {
                            continue;
                        }

                        if (trimmedLine === 'data: [DONE]') {
                            console.log('🏁 收到结束标记: data: [DONE]');
                            return;
                        }

                        if (trimmedLine.startsWith('data:')) {
                            try {
                                // Handle both "data: " and "data:" formats
                                const jsonStr = trimmedLine.startsWith('data: ')
                                    ? trimmedLine.slice(6)
                                    : trimmedLine.slice(5);

                                // Skip [DONE] marker
                                if (jsonStr.trim() === '[DONE]') {
                                    console.log('🏁 收到结束标记: [DONE]');
                                    return;
                                }

                                // Skip empty JSON strings
                                if (!jsonStr.trim()) {
                                    continue;
                                }

                                const data: ChatCompletionChunk = JSON.parse(jsonStr);

                                // Check if choices array exists and has content
                                if (data.choices && Array.isArray(data.choices) && data.choices.length > 0) {
                                    const choice = data.choices[0];
                                    if (choice && choice.delta) {
                                        const content = choice.delta.content;

                                        if (typeof content === 'string' && content.length > 0) {
                                            totalContentLength += content.length;
                                            console.log('📝 输出内容片段:', content.substring(0, 50) + (content.length > 50 ? '...' : ''), `(长度: ${content.length}, 总长度: ${totalContentLength})`);
                                            yield content;
                                        }

                                        // Check if the stream is finished
                                        if (choice.finish_reason) {
                                            console.log('🏁 流式响应结束，原因:', choice.finish_reason);
                                            return;
                                        }
                                    }
                                }
                            } catch (parseError) {
                                // More specific error handling
                                if (parseError instanceof SyntaxError) {
                                    console.warn('JSON parse error for line:', trimmedLine, parseError.message);
                                } else {
                                    console.warn('Failed to parse SSE data:', parseError);
                                }
                                continue;
                            }
                        }
                    }
                }
            } finally {
                reader.releaseLock();
            }
        } catch (error) {
            console.error('❌ streamChatCompletion 发生错误:', {
                error: error,
                message: (error as Error).message,
                stack: (error as Error).stack,
                sessionId: options.sessionId,
                customerId: options.customerId
            });
            if (options.onError) {
                options.onError(error as Error);
            }
            throw error;
        }
    }



    async sendMessage(
        messages: ChatMessage[],
        options: {
            temperature?: number;
            max_tokens?: number;
            customerId?: string;
            sessionId?: string;
        } = {}
    ): Promise<string> {
        try {
            // 获取或生成sessionId
            const sessionId = options.sessionId || SessionManager.getCurrentSessionId();

            const requestBody: ChatCompletionRequest = {
                messages: messages.map(msg => ({
                    role: msg.role,
                    content: msg.content, // 现在支持 string 或 MessageContent[]
                    ...(msg.metadata && { metadata: msg.metadata })
                })),
                stream: false,
                temperature: options.temperature || 0.7,
                max_tokens: options.max_tokens || 2000,
                sessionId: sessionId,
                ...(options.customerId && { customerId: options.customerId }),
            };

            const response = await fetch(this.baseURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // 更安全的数据访问
            if (data && data.choices && Array.isArray(data.choices) && data.choices.length > 0) {
                const choice = data.choices[0];
                if (choice && choice.message && typeof choice.message.content === 'string') {
                    return choice.message.content;
                }
            }

            console.warn('Unexpected response format:', data);
            return '';
        } catch (error) {
            console.error('Error in sendMessage:', error);
            throw error;
        }
    }
}

export const aiChatService = new AIChatService();
